import * as lodash from 'lodash'

export enum BookingFormulaBaseType {
  BookingTotal = 'total',
  GuestPayout = 'guest_payout',
}

export function isBookingFormulaBaseCustomizable(channel: string) {
  const channels = [/^airbnb/i, /^booking/i, /^expedia/i]

  return !!channels.find((t) => t.test(channel))
}

export type BookingFormulaRangeType = 'month' | 'date'
export type BookingFormulaValidityCriteria = 'booked_date' | 'created_date'

export interface BookingFormulaAppliedSchedule {
  rentals: string[]
  months: number[]
  range_type: BookingFormulaRangeType
  start_date?: number | null
  end_date?: number | null
}

export interface BookingFormulaResponse {
  base: BookingFormulaBaseType
  name: string
  formula: BookingFormulaItem[]
  cumulative: number
  rental_id?: string
  status: 0 | 1
  rentals?: string[]
  scheduling?: {
    start_month?: number
    end_month?: number
    range_type?: BookingFormulaRangeType
    /** unix epoch seconds — only used when range_type = 'date' */
    start_date?: number
    /** unix epoch seconds — only used when range_type = 'date' */
    end_date?: number
  }
  /** Controls which booking date is used to match this formula. Default: 'booked_date' */
  validity_criteria?: BookingFormulaValidityCriteria

  lastupdated: number
  creator: string
  account: number
  channel_id: string
  archived: 0 | 1
  deleted: 0 | 1
  pkey: string
  created: number
}

export interface BookingFormulaResponseView extends BookingFormulaResponse {
  channelView: string
  rentalsView: string
  months: string
}

export type CreateBookingFormulaPayload = Omit<
  BookingFormulaResponse,
  'pkey' | 'created' | 'lastupdated' | 'deleted' | 'archived' | 'created' | 'account'
>
export type UpdateBookingFormulaPayload = Partial<CreateBookingFormulaPayload>

export interface BookingFormulaItem {
  amount: number
  type: 'flat' | 'percent' // | 'variable'
  operation: 'add' | 'subtract'
  name: string
}

export function getFormulaScheduling(formula: BookingFormulaResponse) {
  return {
    start_month: +formula.scheduling?.start_month || 0,
    end_month: lodash.isNil(formula.scheduling?.end_month) ? 11 : +formula.scheduling?.end_month,
    range_type: formula.scheduling?.range_type || 'month',
    start_date: formula.scheduling?.start_date ?? null,
    end_date: formula.scheduling?.end_date ?? null,
  }
}

export function getAppliedMonths(items: BookingFormulaResponse[]): BookingFormulaAppliedSchedule[] {
  return lodash.map(
    items.filter((t) => !!t.status),
    (t) => ({
      rentals: t.rentals || [],
      range_type: (t.scheduling?.range_type || 'month') as BookingFormulaRangeType,
      start_date: t.scheduling?.start_date ?? null,
      end_date: t.scheduling?.end_date ?? null,
      months: formulaAppliedMonths(t.scheduling?.start_month, t.scheduling?.end_month),
    })
  )
}

export const formulaAppliedMonths = (start: number, end: number, includeDefault = false) => {
  if (!lodash.isNumber(start) || !lodash.isNumber(end)) {
    return includeDefault ? lodash.range(12) : []
  }

  if (start <= end) {
    return lodash.range(start, end + 1)
  }

  return lodash.range(start, end + 12 + 1).map((t) => t % 12)
}

/**
 * Check whether a new formula's schedule conflicts with existing applied formulas.
 *
 * Rules:
 *  - date-range vs date-range: overlap blocked
 *  - month-range vs month-range: overlap blocked
 *  - date-range vs month-range (cross-type): always allowed
 */
export function isFormulasMonthOverlap(
  appliedItems: BookingFormulaAppliedSchedule[],
  rentals: string[],
  months: number[],
  rangeType: BookingFormulaRangeType = 'month',
  dateRange?: { start_date?: number | null; end_date?: number | null }
): boolean {
  // Only check items of the same range_type — cross-type is always allowed
  const selectedRentals = rentals || []
  const sameTypeItems = appliedItems
    .filter((t) => (t.range_type || 'month') === rangeType)
    .filter((t) => {
      if (!selectedRentals.length) {
        return !t.rentals?.length
      }
      return lodash.intersection(t.rentals, selectedRentals).length > 0
    })

  if (rangeType === 'date') {
    const start = dateRange?.start_date
    const end = dateRange?.end_date
    if (!lodash.isNumber(start) || !lodash.isNumber(end)) {
      return false
    }

    return sameTypeItems.some((t) => {
      if (!lodash.isNumber(t.start_date) || !lodash.isNumber(t.end_date)) {
        return false
      }
      return start <= t.end_date && end >= t.start_date
    })
  }

  const appliedMonths = lodash.uniq(
    lodash.flatten(
      sameTypeItems.map((t) => t.months)
    )
  )

  return lodash.intersection(months, appliedMonths).length > 0
}
